import type { PartnerTenant } from '@shipium-corp/shipium-ts-types';
import type { HydrationAssetError, HydrationAssetErrorMsg } from '../types';

export const isActiveAsset = (asset: any) => {
  return !!asset && asset.active && !asset.archived;
};

// Tenant "links" map an asset (by shipiumId) to the tenant that scopes it. The index is cached
// per tenants-array reference (a hydration pass reuses the same reference) via WeakMap.
type TenantLink = { shipiumId?: string };
const tenantScopeIndexCache = new WeakMap<PartnerTenant[], Map<string, Map<string, PartnerTenant>>>();

const getTenantScopeIndex = (tenants: PartnerTenant[]): Map<string, Map<string, PartnerTenant>> => {
  const cached = tenantScopeIndexCache.get(tenants);
  if (cached) {
    return cached;
  }
  const byAssetType = new Map<string, Map<string, PartnerTenant>>();
  for (const tenant of tenants) {
    const links = (tenant.links ?? {}) as Record<string, TenantLink[]>;
    for (const assetType of Object.keys(links)) {
      let byId = byAssetType.get(assetType);
      if (!byId) {
        byId = new Map();
        byAssetType.set(assetType, byId);
      }
      for (const link of links[assetType] ?? []) {
        // Preserve the original "first matching tenant in array order wins" behavior.
        if (link.shipiumId && !byId.has(link.shipiumId)) {
          byId.set(link.shipiumId, tenant);
        }
      }
    }
  }
  tenantScopeIndexCache.set(tenants, byAssetType);
  return byAssetType;
};

export const findScopedTenant = (assetId: string, assetType: string, tenants: PartnerTenant[]) =>
  getTenantScopeIndex(tenants).get(assetType)?.get(assetId);

export const extractTenantDetails = (tenant: PartnerTenant) => ({
  name: tenant.name,
  tenantId: tenant.tenantId,
});

export const generateHydrationError = (
  errorMessage: HydrationAssetErrorMsg,
  assetType: string,
  assetId?: string,
): HydrationAssetError => {
  return {
    error: errorMessage,
    assetId,
    assetType,
  };
};
